import { formatDate } from '_technical/utils/date';
import React from 'react';
import { TFunction } from 'react-i18next';
import { ReactComponent as StepDescriptionIcon } from 'assets/icons/StepDescription.svg';
import { ReactComponent as StepDrawIcon } from 'assets/icons/StepDraw.svg';
import { ReactComponent as StepEventAvailable } from 'assets/icons/StepEventAvailable.svg';
import { ReactComponent as SearchCalendar } from 'assets/icons/SearchCalendar.svg';
import { ReactComponent as EditCalendar } from 'assets/icons/EditCalendar.svg';
import { Deal } from '_business/types/TransactionDetails';
import * as Sentry from '@sentry/react';

export enum HubspotDealStatus {
  Nouveau = 'Nouveau',
  Contacte = 'Contacté',
  AbandonDeProjet = 'Abandon de projet',
  DevisEnvoye = 'Devis envoyé',
  DevisSigne = 'Devis signé',
  Visite1APlanifier = 'Visite 1 à planifier',
  Visite1Planifiee = 'Visite 1 planifiée',
  ReleveTerrainRealise = 'Relevé terrain réalisé',
  Audit = 'Audit',
  CoherenceDevisAudit = 'Cohérence Devis & Audit',
  ControleDevisCohAudit = 'Contrôle devis & cohérence audit',
  ControleDesDevis = 'Contrôle des devis',
  PlanDeFinancement = 'Plan de financement',
  CompteMprDemandeSubventions = 'Compte MPR et demande de subventions',
  CompteMprDemandeSubvention = 'Compte MPR et demande de subvention',
  AccordSubventionObtenu = 'Accord de subvention obtenu',
  Travaux = 'Travaux',
  CoherenceFacturesProjet = 'Cohérence Factures & Projet',
  VisiteFinaleAOrganiser = 'Visite finale à organiser',
  VisiteFinalePlanifiee = 'Visite finale planifiée',
  MprDemandeSolde = 'MPR demande de solde',
  SubventionsValidees = 'Subventions validées',
  SubventionsPayees = 'Subventions payées',
  AttenteDesDevis = 'Attente des devis',
  AttenteDesFactures = 'Attente des factures',
  FacturesNonConformes = 'Factures non conforme',
  FacturesRecuesAControler = 'Factures reçues à contrôler',
}

export enum DealStatus {
  NouveauProjet = 'Nouveau projet',
  Devis = 'Devis',
  VisiteTechnique = 'Visite technique',
  Audit = 'Audit',
  ControleDesDevis = 'Contrôle des devis',
  DemandeDeSubventions = 'Demande de subventions', // Utilisé uniquement pour l'affichage
  SubventionsAccordees = 'Subventions accordées', // Utilisé uniquement pour l'affichage
  SubventionsDemandeesOuAccordees = 'Subventions demandées ou accordées', // Utilisé uniquement pour les filtres (combine les 2 précédents)
  Travaux = 'Travaux',
  ControleDesFactures = 'Contrôle des factures',
  VisiteFinDeChantier = 'Visite fin de chantier',
  DepotDemandeDeSolde = 'Dépôt demande de solde',
  SoldeMaPrimeRenovValide = "Solde MaPrimeRénov' validé",
  SoldeMaPrimeRenovPaye = "Solde MaPrimeRénov' payé",
}

export const displayDealStatus = [
  DealStatus.NouveauProjet,
  DealStatus.Devis,
  DealStatus.VisiteTechnique,
  DealStatus.Audit,
  DealStatus.ControleDesDevis,
  DealStatus.DemandeDeSubventions,
  DealStatus.SubventionsAccordees,
  DealStatus.Travaux,
  DealStatus.ControleDesFactures,
  DealStatus.VisiteFinDeChantier,
  DealStatus.DepotDemandeDeSolde,
  DealStatus.SoldeMaPrimeRenovValide,
  DealStatus.SoldeMaPrimeRenovPaye,
];

export const filterDealStatus = [
  DealStatus.NouveauProjet,
  DealStatus.Devis,
  DealStatus.VisiteTechnique,
  DealStatus.Audit,
  DealStatus.ControleDesDevis,
  DealStatus.SubventionsDemandeesOuAccordees,
  DealStatus.Travaux,
  DealStatus.ControleDesFactures,
  DealStatus.VisiteFinDeChantier,
  DealStatus.DepotDemandeDeSolde,
  DealStatus.SoldeMaPrimeRenovValide,
  DealStatus.SoldeMaPrimeRenovPaye,
];

export const hubspotDealStatus = [
  HubspotDealStatus.Nouveau,
  HubspotDealStatus.Contacte,
  HubspotDealStatus.DevisEnvoye,
  HubspotDealStatus.DevisSigne,
  HubspotDealStatus.Visite1APlanifier,
  HubspotDealStatus.Visite1Planifiee,
  HubspotDealStatus.ReleveTerrainRealise,
  HubspotDealStatus.Audit,
  HubspotDealStatus.CoherenceDevisAudit,
  HubspotDealStatus.PlanDeFinancement,
  HubspotDealStatus.CompteMprDemandeSubventions,
  HubspotDealStatus.CompteMprDemandeSubvention,
  HubspotDealStatus.AccordSubventionObtenu,
  HubspotDealStatus.Travaux,
  HubspotDealStatus.CoherenceFacturesProjet,
  HubspotDealStatus.VisiteFinaleAOrganiser,
  HubspotDealStatus.VisiteFinalePlanifiee,
  HubspotDealStatus.MprDemandeSolde,
  HubspotDealStatus.SubventionsValidees,
  HubspotDealStatus.SubventionsPayees,
];

export function mapDealStatus(
  status: HubspotDealStatus,
  sous_etape_hi?: string,
): DealStatus {
  switch (status) {
    case HubspotDealStatus.Nouveau:
    case HubspotDealStatus.Contacte:
      return DealStatus.NouveauProjet;
    case HubspotDealStatus.DevisEnvoye:
    case HubspotDealStatus.DevisSigne:
      return DealStatus.Devis;
    case HubspotDealStatus.Visite1APlanifier:
    case HubspotDealStatus.Visite1Planifiee:
      return DealStatus.VisiteTechnique;
    case HubspotDealStatus.ReleveTerrainRealise:
    case HubspotDealStatus.Audit:
      return DealStatus.Audit;
    case HubspotDealStatus.ControleDesDevis:
    case HubspotDealStatus.PlanDeFinancement:
    case HubspotDealStatus.CoherenceDevisAudit:
    case HubspotDealStatus.ControleDevisCohAudit:
      return DealStatus.ControleDesDevis;
    case HubspotDealStatus.CompteMprDemandeSubventions:
      if (sous_etape_hi === HubspotDealStatus.AccordSubventionObtenu) {
        return DealStatus.SubventionsAccordees;
      }
      return DealStatus.DemandeDeSubventions;
    case HubspotDealStatus.AccordSubventionObtenu:
      return DealStatus.SubventionsAccordees;
    case HubspotDealStatus.Travaux:
      return DealStatus.Travaux;
    case HubspotDealStatus.CoherenceFacturesProjet:
      return DealStatus.ControleDesFactures;
    case HubspotDealStatus.VisiteFinaleAOrganiser:
    case HubspotDealStatus.VisiteFinalePlanifiee:
      return DealStatus.VisiteFinDeChantier;
    case HubspotDealStatus.MprDemandeSolde:
      return DealStatus.DepotDemandeDeSolde;
    case HubspotDealStatus.SubventionsValidees:
      return DealStatus.SoldeMaPrimeRenovValide;
    case HubspotDealStatus.SubventionsPayees:
      return DealStatus.SoldeMaPrimeRenovPaye;
    default:
      Sentry.captureException(new Error('Hubspot Deal status not found'), {
        extra: {
          status,
          sous_etape_hi,
        },
      });
      return DealStatus.NouveauProjet;
  }
}

export function mapDealStatusToColorStyle(status: HubspotDealStatus): string {
  switch (status) {
    case HubspotDealStatus.AbandonDeProjet:
      return 'bg-RougeHellio-2 text-RougeHellio-10';
    default:
      return 'bg-VertHellio-2 text-VertHellio-10';
  }
}

export interface TagStatus {
  label: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  color: 'orange' | 'green';
}

export function mapDealStateToTag(
  t: TFunction<'project', undefined>,
  deal: Deal,
  status: HubspotDealStatus,
): TagStatus | undefined {
  if (
    getHubspotDealStatusIndex(deal.dealstage.label) <
    getHubspotDealStatusIndex(status)
  ) {
    return undefined;
  }
  switch (status) {
    case HubspotDealStatus.DevisEnvoye:
      return {
        label: t('status.available'),
        color: 'green',
        icon: StepDescriptionIcon,
      };
    case HubspotDealStatus.DevisSigne:
      return {
        label: t('status.signed'),
        color: 'green',
        icon: StepDrawIcon,
      };
    case HubspotDealStatus.Visite1APlanifier:
      return {
        label: t('status.to_plan'),
        color: 'orange',
        icon: EditCalendar,
      };
    case HubspotDealStatus.Visite1Planifiee:
      if (!deal.date_de_la_visite_1_mar) {
        return undefined;
      }
      return {
        label: t('status.planned', {
          date: formatDate(deal.date_de_la_visite_1_mar, 'dd MMMM yyyy'),
        }),
        color: 'green',
        icon: StepEventAvailable,
      };
    case HubspotDealStatus.ReleveTerrainRealise:
      return {
        label: t('status.in_progress'),
        color: 'orange',
        icon: SearchCalendar,
      };
    case HubspotDealStatus.Audit:
      return {
        label: t('status.available'),
        color: 'green',
        icon: StepDescriptionIcon,
      };
    case HubspotDealStatus.VisiteFinaleAOrganiser:
      return {
        label: t('status.to_plan'),
        color: 'orange',
        icon: EditCalendar,
      };
    case HubspotDealStatus.VisiteFinalePlanifiee:
      if (!deal.date_visite_finale) {
        return undefined;
      }
      return {
        label: t('status.planned', {
          date: formatDate(deal.date_visite_finale, 'dd MMMM yyyy'),
        }),
        color: 'green',
        icon: StepEventAvailable,
      };
    case HubspotDealStatus.Travaux:
      if (!deal.date_previsionnelle_de_fin_de_chantier) {
        return undefined;
      }
      return {
        label: t('status.work_end', {
          date: formatDate(
            deal.date_previsionnelle_de_fin_de_chantier,
            'dd MMMM yyyy',
          ),
          type: t('status_type.work_end'),
        }),
        color: 'green',
        icon: StepEventAvailable,
      };
    default:
      return undefined;
  }
}

export function getDisplayDealStatusIndex(
  status: HubspotDealStatus,
  sous_etape_hi?: string,
): number {
  return displayDealStatus.indexOf(mapDealStatus(status, sous_etape_hi));
}

export function getHubspotDealStatusIndex(status: HubspotDealStatus): number {
  return hubspotDealStatus.indexOf(status);
}
